import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { isMobileOnly, MobileView } from 'react-device-detect';
import styled from 'styled-components';
import { EmptyDiv, HorizontalDiv, VerticalDiv } from '../../../../../components/Common';
import ConfirmDialog from '../../../../../components/ConfirmDialog';
import ExpandIcon from '../../../../../components/ExpandIcon';
import InfoText from '../../../../../components/InfoText';
import ListItem from '../../../../../components/ListItem';
import PhotoCarousel from '../../../../../components/PhotoCarousel';
import {
    CAC_TYPE,
    EDIT,
    DELETE,
    ESTIMATED_SEEDLINGS,
    ESTIMATED_YIELD,
    EST_SEEDLINGS,
    EST_YIELD
} from '../../../../../res/Constants';
import colors from '../../../../../res/styles/Colors';
import dimension from '../../../../../res/styles/Responsive';
import themeButton from '../../../../../res/styles/theme_button.module.scss';
import { extractLinesData } from '../../../../../service/ReportService';
import { getHarvestLines } from '../../../../../utils/Utils';
import ReportLine from './ReportLine';

const pictureIcon = require('../../../../../res/images/cac_picture_icon_report_black.png');

ReportItem.propTypes = {
    className: PropTypes.string,
    linesCount: PropTypes.number,
    reportData: PropTypes.object,
    cacType: PropTypes.number,
    isEditable: PropTypes.bool.isRequired,
    onEdit: PropTypes.func,
    onDelete: PropTypes.func,
    photosList: PropTypes.arrayOf(PropTypes.object),
    contractHarvest: PropTypes.string
};

ReportItem.defaultProps = {
    onEdit: () => { },
    onDelete: () => { },
    photosList: []
};

function ReportItem({ className, cacType, linesCount, reportData, isEditable, onEdit, onDelete, photosList, contractHarvest }) {
    const [carouselVisible, setCarouselVisible] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const reportDate = moment(reportData.date).locale('it').format('DD/MMM/YYYY');
    const linesData = extractLinesData(linesCount, reportData);
    const photosCount = linesData?.lineA?.photosCount ?? 0;

    // Get the correct line names based on harvest type
    const lineNames = getHarvestLines(cacType, contractHarvest);

    // Filter photos by reportId
    const reportPhotos = photosList.filter((photo) => {
        return photo.reportId === reportData.pk;
    });

    const toggleCarousel = () => {
        if (reportPhotos.length > 0) {
            setCarouselVisible(!carouselVisible);
        }
    };

    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = (reportPk, confirmed) => {
        setShowDeleteConfirm(false);
        if (confirmed) {
            onDelete(reportPk);
        }
    };

    const viewSettings = [
        {
            color: colors.green,
            bgColor: colors.lighter_green
        },
        {
            color: colors.red,
            bgColor: colors.lighter_red
        }
    ];

    const expandableChildren = (
        <VerticalDiv>
            <HorizontalDiv>
                {Object.entries(linesData)
                    .filter((entries) => entries[0] !== 'lineU')
                    .map((entries) => entries[1])
                    .map((objModel, index) => {
                        // Override the lineName with the correct one from getHarvestLines
                        const updatedLineData = {
                            ...objModel,
                            lineName: lineNames[index] || objModel.lineName
                        };

                        return (
                            <HorizontalDiv style={{ width: '100%' }} key={index}>
                                <MobileView renderWithFragment>
                                    {index === 1 && (
                                        <VerticalDivisorContainer>
                                            <VerticalDivisor width='2px' height='100%' />{' '}
                                        </VerticalDivisorContainer>
                                    )}
                                </MobileView>
                                <FullHeightReportLine
                                    cacType={cacType}
                                    reportLineData={updatedLineData}
                                    {...viewSettings[index]}
                                />
                            </HorizontalDiv>
                        );
                    })}
            </HorizontalDiv>
            {(!isMobileOnly || isEditable) && (
                <FooterContainer>
                    {!isMobileOnly && (
                        <>
                            {reportData.techId && reportData.techId != '-' && (
                                <InfoText title='Reported by' content={reportData.techId} />
                            )}
                            <InfoText
                                title={
                                    cacType === CAC_TYPE.CROP ? ESTIMATED_YIELD : ESTIMATED_SEEDLINGS
                                }
                                content={reportData.expYield}
                            />
                        </>
                    )}
                    {cacType === CAC_TYPE.NURSERY && (
                        <InfoText title='Harvest Data' content={reportData.harvestData}></InfoText>
                    )}
                    {isEditable && (
                        <ButtonsContainer>
                            <EditButton onClick={onEdit}>{EDIT.toUpperCase()}</EditButton>
                            <DeleteButton onClick={handleDeleteClick}>{DELETE.toUpperCase()}</DeleteButton>
                        </ButtonsContainer>
                    )}
                </FooterContainer>
            )}
        </VerticalDiv>
    );

    return (
        <div className={className}>
            <StyledListItem expandableChildren={expandableChildren} isExpanded={!isMobileOnly}>
                {({ toggleExpand }) => (
                    <TitleContainer>
                        <DateAndPhotosContainer>
                            <h6>{reportDate}</h6>
                            <PhotosButton
                                onClick={toggleCarousel}
                                clickable={reportPhotos.length > 0}
                                disabled={reportPhotos.length === 0}
                            >
                                <img src={pictureIcon} alt="photos" />
                                <p style={{ marginBottom: '0' }}>{photosCount}</p>
                            </PhotosButton>
                        </DateAndPhotosContainer>
                        {isMobileOnly && (
                            <>
                                {reportData.techId && reportData.techId != '-' && (
                                    <InfoText title='Reported by' content={reportData.techId} />
                                )}
                                <InfoText
                                    title={cacType === CAC_TYPE.CROP ? EST_YIELD : EST_SEEDLINGS}
                                    content={reportData.expYield}
                                />
                                <ExpandIcon
                                    alt='show the report information'
                                    onClick={toggleExpand}
                                />
                            </>
                        )}
                    </TitleContainer>
                )}
            </StyledListItem>
            <PhotoCarousel
                isVisible={carouselVisible}
                photosList={reportPhotos}
                onClose={toggleCarousel}
            />
            <ConfirmDialog
                isOpen={showDeleteConfirm}
                text="Are you sure you want to delete this report? Linked images will be deleted."
                elementId={reportData.pk}
                onConfirm={handleDeleteConfirm}
            />
        </div>
    );
}

const DateAndPhotosContainer = styled(HorizontalDiv)`
    align-items: center;
    
    h6 {
        margin: 0;
        padding: 0;
    }
`;

const PhotosButton = styled.button`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 3px;
    background: transparent;
    border: none;
    cursor: ${props => props.clickable ? 'pointer' : 'default'};
    transition: all 0.15s ease;
    padding: 2px 4px;
    margin: 0;
    border-radius: 3px;
    margin-left: 5px;
    
    @media ${dimension.md} {
        margin-left: 20px;
    }
    
    &:hover:not(:disabled) {
        background-color: ${colors.light_gray};
    }
    
    &:active:not(:disabled) {
        opacity: 0.7;
    }
    
    &:disabled {
        cursor: default;
        opacity: 1;
    }
    
    img {
        width: 15px;
        height: 15px;

        @media ${dimension.md} {
            margin-left: 4px;
            margin-right: 2px;
        }
    }

    p {
        line-height: initial;
        margin: 0;
    }
`;

const FullHeightReportLine = styled(ReportLine)`
    width: 100%;
    height: auto;
`;

const StyledListItem = styled(ListItem)`
    .inner-container-expandable {
        padding: 0;

        @media ${dimension.md} {
            padding: 0 5% 15px;
        }
    }
`;

const TitleContainer = styled(HorizontalDiv)`
    justify-content: space-between;

    @media ${dimension.md} {
        justify-content: center;
    }
`;

const FooterContainer = styled(HorizontalDiv)`
    justify-content: space-between;
    padding: 15px;
    background-color: white;

    @media ${dimension.md} {
        background-color: inherit;
    }
`;

const ButtonsContainer = styled(HorizontalDiv)`
    gap: 10px;
    margin: auto;
    justify-content: center;
    width: 100%;

    @media ${dimension.md} {
        margin: 0;
        width: auto;
    }
`;

const EditButton = styled.button.attrs({
    className: themeButton.red
})`
    width: 150px;
    min-width: 150px;
    flex-shrink: 0;
    
    @media (max-width: 767px) {
        width: 45%;
        min-width: 120px;
        max-width: 150px;
    }
`;

const DeleteButton = styled.button.attrs({
    className: themeButton.red
})`
    width: 150px;
    min-width: 150px;
    flex-shrink: 0;
    
    @media (max-width: 767px) {
        width: 45%;
        min-width: 120px;
        max-width: 150px;
    }
`;

const VerticalDivisorContainer = styled(EmptyDiv)`
    width: auto;
    height: auto;
    display: flex;
    flex-shrink: 0;

    @media ${dimension.md} {
        padding: 0 15px;
    }
`;

const VerticalDivisor = styled(EmptyDiv)`
    margin: auto 0;
    background-color: lightgray;
`;

export default ReportItem;
